import type { GenerateResponse, RenderedSection } from "@unbox/api";
import { useState } from "react";
import { client, useScopedMerchantClient } from "@/api/scoped-merchant-client";
import Icon from "./Icon";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function markdownToHtml(md: string): string {
  // Very lightweight: escape HTML first (the body is LLM-generated and may
  // contain `<`, `&`, or stray markup), then bold, code, bullets, newlines.
  // Escaping must precede the tag insertions below so our own tags survive.
  // Kept in sync with the server renderer's mdToHtml in the report HTML export.
  return md
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(
      /`([^`]+)`/g,
      '<code style="font-family:var(--font-mono);font-size:0.9em;background:var(--color-gray-100);padding:1px 4px;border-radius:3px;">$1</code>',
    )
    .replace(/^- /gm, "<br/>• ")
    .replace(/\n/g, "<br/>");
}

// ---------------------------------------------------------------------------
// Section renderers
// ---------------------------------------------------------------------------

function DocHeader({ section }: { section: Extract<RenderedSection, { type: "doc_header" }> }) {
  return (
    <div
      style={{
        paddingBottom: 16,
        borderBottom: "1px solid var(--color-border-secondary)",
        marginBottom: 20,
      }}
    >
      <h2
        style={{
          fontSize: 18,
          fontWeight: 600,
          color: "var(--color-text-primary)",
          margin: "0 0 12px",
          letterSpacing: "-0.01em",
          lineHeight: "24px",
        }}
      >
        {section.title}
      </h2>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "6px 20px",
        }}
      >
        {section.metadata.map((m) => (
          <div key={m.label} style={{ fontSize: 12 }}>
            <span style={{ color: "var(--color-text-quaternary)", textTransform: "capitalize" }}>{m.label}: </span>
            <span style={{ color: "var(--color-text-secondary)", fontWeight: 500 }}>{m.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Callout({ section }: { section: Extract<RenderedSection, { type: "callout" }> }) {
  return (
    <div
      style={{
        background: "var(--color-brand-50)",
        border: "1px solid var(--color-brand-200)",
        borderRadius: 8,
        padding: "10px 14px",
        marginBottom: 16,
        fontSize: 13,
        lineHeight: "19px",
        color: "var(--color-text-secondary)",
      }}
    >
      {section.title && (
        <div style={{ fontWeight: 600, color: "var(--color-brand-700)", marginBottom: 4, fontSize: 12 }}>
          {section.title}
        </div>
      )}
      {/* biome-ignore lint/security/noDangerouslySetInnerHtml: markdownToHtml escapes HTML before inserting its own tags */}
      <span dangerouslySetInnerHTML={{ __html: markdownToHtml(section.body) }} />
    </div>
  );
}

function HeadlineMetrics({ section }: { section: Extract<RenderedSection, { type: "headline_metrics" }> }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(140px, 100%), 1fr))",
        gap: 10,
        marginBottom: 20,
      }}
    >
      {section.metrics.map((m) => (
        <div
          key={m.label}
          style={{
            background: m.warn ? "var(--color-warning-50)" : "var(--color-bg-primary)",
            border: `1px solid ${m.warn ? "var(--color-warning-100)" : "var(--color-border-secondary)"}`,
            borderRadius: 10,
            padding: "12px 14px",
            boxShadow: "var(--shadow-xs)",
            minWidth: 0,
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 500,
              color: m.warn ? "var(--color-warning-700)" : "var(--color-text-quaternary)",
              marginBottom: 4,
            }}
          >
            {m.label}
          </div>
          <div
            style={{
              fontSize: 22,
              fontWeight: 600,
              color: m.warn ? "var(--color-warning-700)" : "var(--color-text-primary)",
              fontVariantNumeric: "tabular-nums",
              letterSpacing: "-0.01em",
              lineHeight: "28px",
            }}
          >
            {m.value}
          </div>
          {m.suffix && (
            <div
              style={{
                fontSize: 11,
                color: m.warn ? "var(--color-warning-700)" : "var(--color-text-quaternary)",
                marginTop: 3,
                opacity: 0.85,
              }}
            >
              {m.suffix}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function DataTable({ section }: { section: Extract<RenderedSection, { type: "table" }> }) {
  return (
    <div style={{ marginBottom: 20 }}>
      {section.title && (
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "var(--color-text-primary)",
            marginBottom: 8,
          }}
        >
          {section.title}
        </div>
      )}
      <div
        style={{
          border: "1px solid var(--color-border-secondary)",
          borderRadius: 8,
          overflow: "hidden",
          fontSize: 13,
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr
                style={{ background: "var(--color-gray-50)", borderBottom: "1px solid var(--color-border-secondary)" }}
              >
                {section.columns.map((col) => (
                  <th
                    key={col.id}
                    style={{
                      padding: "7px 12px",
                      fontSize: 11,
                      fontWeight: 600,
                      color: "var(--color-text-quaternary)",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      textAlign: (col.align as "left" | "right" | "center") ?? "left",
                      whiteSpace: "nowrap",
                      verticalAlign: "bottom",
                    }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={section.columns.length}
                    style={{
                      padding: "20px 12px",
                      textAlign: "center",
                      color: "var(--color-text-quaternary)",
                      fontSize: 12,
                    }}
                  >
                    {section.noDataMessage ?? "No data"}
                  </td>
                </tr>
              ) : (
                section.rows.map((row, i) => {
                  const value = (col: { id: string }) => row[col.id] || "—";
                  return (
                    <tr
                      // biome-ignore lint/suspicious/noArrayIndexKey: rows are static render-once data with no identity column
                      key={i}
                      style={{
                        borderBottom: i < section.rows.length - 1 ? "1px solid var(--color-border-tertiary)" : "none",
                        background: i % 2 === 1 ? "var(--color-gray-25)" : "var(--color-bg-primary)",
                      }}
                    >
                      {section.columns.map((col) => {
                        const isNumeric = col.align === "right";
                        const cellValue = value(col);
                        return (
                          <td
                            key={col.id}
                            title={!isNumeric ? cellValue : undefined}
                            style={{
                              padding: "7px 12px",
                              color: "var(--color-text-secondary)",
                              textAlign: (col.align as "left" | "right" | "center") ?? "left",
                              fontVariantNumeric: isNumeric ? "tabular-nums" : undefined,
                              whiteSpace: isNumeric ? "nowrap" : undefined,
                            }}
                          >
                            {isNumeric ? (
                              cellValue
                            ) : (
                              <div
                                style={{
                                  maxWidth: 160,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {cellValue}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function RecommendationPick({ section }: { section: Extract<RenderedSection, { type: "recommendation_pick" }> }) {
  return (
    <div
      style={{
        background: "var(--color-brand-50)",
        border: "1px solid var(--color-brand-300)",
        borderRadius: 10,
        padding: "12px 16px",
        marginBottom: 16,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 7,
            background: "var(--color-brand-600)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            marginTop: 1,
          }}
        >
          <Icon name="sparkle" size={14} color="#fff" strokeWidth={1.75} />
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--color-text-brand-primary)", marginBottom: 4 }}>
            {section.headline}
          </div>
          <div
            style={{ fontSize: 13, color: "var(--color-text-secondary)", lineHeight: "19px" }}
            // biome-ignore lint/security/noDangerouslySetInnerHtml: markdownToHtml escapes HTML before inserting its own tags
            dangerouslySetInnerHTML={{ __html: markdownToHtml(section.body) }}
          />
        </div>
      </div>
    </div>
  );
}

function CaveatBlock({ section }: { section: Extract<RenderedSection, { type: "caveat_block" }> }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      style={{
        background: "var(--color-warning-50)",
        border: "1px solid var(--color-warning-100)",
        borderRadius: 8,
        marginBottom: 12,
        overflow: "hidden",
      }}
    >
      <button
        onClick={() => setExpanded((v) => !v)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "9px 12px",
          background: "none",
          border: "none",
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <Icon name="shield" size={13} color="var(--color-warning-600)" strokeWidth={1.75} />
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--color-warning-700)" }}>{section.title}</span>
        </div>
        <Icon name="chevronDown" size={12} color="var(--color-warning-600)" strokeWidth={2} />
      </button>
      {expanded && (
        <div
          style={{
            padding: "0 12px 10px",
            fontSize: 12,
            lineHeight: "18px",
            color: "var(--color-warning-700)",
            borderTop: "1px solid var(--color-warning-100)",
            paddingTop: 8,
          }}
          // biome-ignore lint/security/noDangerouslySetInnerHtml: markdownToHtml escapes HTML before inserting its own tags
          dangerouslySetInnerHTML={{ __html: markdownToHtml(section.body) }}
        />
      )}
    </div>
  );
}

function Commentary({ section }: { section: Extract<RenderedSection, { type: "commentary" }> }) {
  const isKeyFinding = section.style === "key_finding";

  if (isKeyFinding) {
    return (
      <div
        style={{
          background: "var(--color-brand-50)",
          border: "1px solid var(--color-brand-200)",
          borderLeft: "3px solid var(--color-brand-600)",
          borderRadius: "0 8px 8px 0",
          padding: "10px 14px",
          marginBottom: 20,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "var(--color-brand-700)",
            marginBottom: 5,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          {section.title ?? "Key finding"}
        </div>
        <p
          style={{ fontSize: 13, lineHeight: "20px", color: "var(--color-text-secondary)", fontWeight: 500, margin: 0 }}
          // biome-ignore lint/security/noDangerouslySetInnerHtml: markdownToHtml escapes HTML before inserting its own tags
          dangerouslySetInnerHTML={{ __html: markdownToHtml(section.body) }}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        borderLeft: "3px solid var(--color-gray-300)",
        paddingLeft: 14,
        marginBottom: 20,
        marginTop: -8,
      }}
    >
      {section.title && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "var(--color-text-quaternary)",
            marginBottom: 5,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          {section.title}
        </div>
      )}
      <p
        style={{
          fontSize: 13,
          lineHeight: "20px",
          color: "var(--color-text-secondary)",
          fontStyle: "italic",
          margin: 0,
        }}
        // biome-ignore lint/security/noDangerouslySetInnerHtml: markdownToHtml escapes HTML before inserting its own tags
        dangerouslySetInnerHTML={{ __html: markdownToHtml(section.body) }}
      />
    </div>
  );
}

function Interpretation({ section }: { section: Extract<RenderedSection, { type: "interpretation" }> }) {
  return (
    <div
      style={{
        borderLeft: "3px solid var(--color-brand-600)",
        paddingLeft: 14,
        marginTop: 8,
        marginBottom: 4,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: "var(--color-brand-700)",
          marginBottom: 6,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        Interpretation
      </div>
      <p
        style={{
          fontSize: 13,
          lineHeight: "19px",
          color: "var(--color-text-secondary)",
          fontStyle: "italic",
          margin: 0,
        }}
      >
        {section.text}
      </p>
      <div style={{ fontSize: 11, color: "var(--color-text-quaternary)", marginTop: 6 }}>
        Hypothesis framing · not deterministic
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReportViewer — wraps all sections with a header bar + download buttons
// ---------------------------------------------------------------------------

type ExportFormat = "html" | "markdown" | "csv";

const EXPORTS: { format: ExportFormat; ext: string; label: string }[] = [
  { format: "html", ext: "html", label: "Download HTML" },
  { format: "markdown", ext: "md", label: "Download .md" },
  { format: "csv", ext: "csv", label: "Download .csv" },
];

export default function ReportViewer({ report }: { report: GenerateResponse }) {
  const scope = useScopedMerchantClient();
  const [downloading, setDownloading] = useState<ExportFormat | null>(null);

  function postExport(format: ExportFormat) {
    if (scope.workspaceId) {
      return client.workspace[":workspaceId"].merchant[":merchantId"].ask.report[format].$post({
        param: scope.param,
        json: report,
      });
    }
    return client.merchant[":merchantId"].ask.report[format].$post({ param: scope.param, json: report });
  }

  async function handleDownload(format: ExportFormat, ext: string) {
    setDownloading(format);
    try {
      const res = await postExport(format);
      if (!res.ok) throw new Error("Failed to generate export");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${report.merchantId}-${report.templateId}-${report.generatedAt}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // silently fail — user can try again
    } finally {
      setDownloading(null);
    }
  }

  return (
    <div
      className="ask-report"
      style={{
        background: "var(--color-bg-primary)",
        border: "1px solid var(--color-border-secondary)",
        borderRadius: 12,
        overflow: "hidden",
        width: "100%",
        minWidth: 0,
        overflowWrap: "anywhere",
      }}
    >
      {/* Header bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 8,
          padding: "10px 16px",
          background: "var(--color-gray-50)",
          borderBottom: "1px solid var(--color-border-secondary)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <Icon name="layers" size={14} color="var(--color-brand-700)" strokeWidth={1.75} />
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-secondary)" }}>
            {report.templateTitle}
          </span>
          <span
            style={{
              fontSize: 11,
              padding: "1px 6px",
              borderRadius: 9999,
              background: "var(--color-gray-200)",
              color: "var(--color-text-quaternary)",
              fontFamily: "var(--font-mono)",
              flexShrink: 0,
            }}
          >
            v{report.templateVersion}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          {EXPORTS.map(({ format, ext, label }) => (
            <button
              key={format}
              onClick={() => handleDownload(format, ext)}
              disabled={downloading !== null}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                padding: "5px 10px",
                border: "1px solid var(--color-border-secondary)",
                borderRadius: 6,
                background: "var(--color-bg-primary)",
                cursor: downloading !== null ? "default" : "pointer",
                fontSize: 12,
                fontWeight: 500,
                color: "var(--color-text-secondary)",
                fontFamily: "inherit",
                whiteSpace: "nowrap",
                opacity: downloading !== null ? 0.6 : 1,
              }}
            >
              <Icon name="download" size={12} color="currentColor" strokeWidth={2} />
              {downloading === format ? "Generating…" : label}
            </button>
          ))}
        </div>
      </div>

      {/* Error banner */}
      {report.error && (
        <div
          style={{
            padding: "8px 16px",
            background: "var(--color-error-50)",
            borderBottom: "1px solid var(--color-error-100)",
            fontSize: 12,
            color: "var(--color-error-700)",
          }}
        >
          <strong>Query error:</strong> {report.error} — some sections may be incomplete.
        </div>
      )}

      {/* Sections */}
      <div style={{ padding: "16px 20px" }}>
        {report.sections.map((section) => {
          switch (section.type) {
            case "doc_header":
              return <DocHeader key={section.id} section={section} />;
            case "callout":
              return <Callout key={section.id} section={section} />;
            case "headline_metrics":
              return <HeadlineMetrics key={section.id} section={section} />;
            case "table":
              return <DataTable key={section.id} section={section} />;
            case "recommendation_pick":
              return <RecommendationPick key={section.id} section={section} />;
            case "caveat_block":
              return <CaveatBlock key={section.id} section={section} />;
            case "commentary":
              return <Commentary key={section.id} section={section} />;
            case "interpretation":
              return <Interpretation key={section.id} section={section} />;
            default:
              return null;
          }
        })}
      </div>
    </div>
  );
}
